import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VoiceRecorder = ({ onTranscription }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        clearInterval(timerRef.current);

        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) {
          toast.error('Recording too short — try again');
          return;
        }

        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'voice-note.webm');
          const response = await axios.post(`${API}/voice/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000
          });
          if (response.data.text) {
            onTranscription(response.data.text);
            toast.success('Voice note transcribed');
          } else {
            toast.error('No speech detected — try again');
          }
        } catch (error) {
          const msg = error.response?.data?.detail || error.message;
          toast.error('Transcription failed: ' + msg);
        } finally {
          setIsTranscribing(false);
          setDuration(0);
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Microphone access denied — please allow microphone in your browser settings');
      } else {
        toast.error('Could not access microphone');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (isTranscribing) {
    return (
      <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="voice-transcribing">
        <Loader2 size={20} className="animate-spin text-indigo-600" />
        <span className="text-sm font-medium text-indigo-700">Transcribing your voice note...</span>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="voice-recording">
        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm font-medium text-red-700">Recording... {formatTime(duration)}</span>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          onClick={stopRecording}
          data-testid="stop-recording-btn"
          className="ml-auto"
        >
          <Square size={14} className="mr-1" />
          Stop & Transcribe
        </Button>
      </div>
    );
  }

  return (
    <Button
      type="button"
      onClick={startRecording}
      data-testid="start-recording-btn"
      className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg transition-all"
    >
      <Mic size={18} className="mr-2" />
      Record Voice Note
    </Button>
  );
};

export default VoiceRecorder;
